import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';

const ConfirmContext = createContext(null);

// window.confirm() is silently ignored by browsers inside cross-origin iframes
// (e.g. the admin embedded in NexusCMS), so all confirmations go through this dialog.
export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    const config = typeof options === 'string' ? { description: options } : options || {};
    return new Promise((resolve) => {
      resolver.current = resolve;
      setRequest(config);
    });
  }, []);

  const settle = (result) => {
    setRequest(null);
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(result);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!request} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-dialog-title">
              {request?.title || 'Are you sure?'}
            </AlertDialogTitle>
            {request?.description && (
              <AlertDialogDescription data-testid="confirm-dialog-description">
                {request.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)} data-testid="confirm-dialog-cancel">
              {request?.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={request?.destructive === false ? '' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-dialog-confirm"
            >
              {request?.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider');
  return confirm;
}
